import {
  keepPreviousData,
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
  useSuspenseQuery,
} from '@tanstack/react-query';
import { create, keyResolver, windowedFiniteBatchScheduler } from '@yornaath/batshit';
import { useTranslation } from 'react-i18next';
import {
  bulkTransactionUpdate,
  createTransaction,
  deleteTransaction,
  exportCashbook,
  fetchSyncedDepositTransactions,
  fetchTransaction,
  fetchTransactionMetadata,
  fetchTransactionProposedRules,
  fetchTransactionReconciliationProposal,
  fetchTransactions,
  fetchTransactionsMetadataPreview,
  fetchTransactionsPreview,
  markDeduplicationOutcome,
  shareCashbook,
  updateTransaction,
} from '~/api/transaction';
import { queryClient } from '~/client/queryClient';
import { snack } from '~/hooks/ToastProvider';
import { useActiveCompanyId } from '~/hooks/useActiveCompany';
import { useAnalytics } from '~/hooks/useAnalytics';
import { captureException } from '~/hooks/useExceptionLogger';
import { AccountQueryKeys } from '~/types/account';
import { CashFlowQueryKeys } from '~/types/cashflow';
import { Category, Subcategory } from '~/types/category';
import { Company } from '~/types/company';
import { ConsentQueryKeys } from '~/types/consent';
import { TransactionRuleDirection } from '~/types/rule';
import {
  CashbookExportFormat,
  Transaction,
  TransactionCreatePayload,
  TransactionMeta,
  TransactionMetadataFilter,
  TransactionSorting,
  TransactionsFiltersFields,
  TransactionsKeys,
  TransactionsPreviewFilter,
  VerificationStatus,
  ShareCashbookParameters,
} from '~/types/transaction';
import { assertIsDefined } from '~/types/typeGuards';
import { downloadBlob } from '~/utils/download';
import { PaginationInfo, SortInfo } from '~/utils/jsonapi-query';
import { AnalyticsEvents } from './analytics';
import { decorateWithReconciliations } from './reconciliation';

const TransactionsPageSize = 50;
const TransactionsPreviewPageSize = 20;

const defaultValues: TransactionsFiltersFields = {
  search: '',
  date: [null, null],
  currency: null,
  accountId: [],
  status: null,
  verificationStatus: null,
  category: null,
  amountRange: [null, null, null],
  hidden: null,
  transactionType: [],
  cardHolder: [],
};

const fetchTransactionDecorated = async (id: Transaction['id']): Promise<Transaction> => {
  const transaction = await fetchTransaction(id);
  const result = await decorateWithReconciliations(
    [transaction],
    ['flow', 'flow.bookkeepingEntry', 'flow.document', 'flow.document.counterpart'],
  );

  return result[0]!;
};

const fetchTransactionsDecorated = async (
  companyId: Company['id'],
  filters: TransactionsFiltersFields,
  sorting: SortInfo<TransactionSorting>,
  pagination: PaginationInfo,
): Promise<{
  data: Transaction[];
  meta: TransactionMeta;
}> => {
  const { data: transactions, meta } = await fetchTransactions({ companyId, ...filters }, sorting, pagination);
  const transactionsWithRiconciliations = await decorateWithReconciliations(transactions, ['transaction']);
  return { data: transactionsWithRiconciliations, meta };
};

export const useExportCashbook = () => {
  const { t } = useTranslation('cashflow');
  const { track } = useAnalytics();
  const companyId = useActiveCompanyId();

  return useMutation({
    mutationFn: async ({
      filename,
      format,
      ...filters
    }: { filename: string; format?: CashbookExportFormat } & TransactionsFiltersFields) => {
      const { data } = await exportCashbook({ companyId, ...filters }, { sort: 'date', direction: 'desc' }, format);
      return { data, filename };
    },
    onError: () => snack.error(t('messages.failed_cashflow_export')),
    onSuccess: ({ data, filename }) => downloadBlob(data, filename),
    onSettled: () => track(AnalyticsEvents.CASHBOOK_EXPORTED),
  });
};

export const useToggleTransactionVisibility = () => {
  const queryClient = useQueryClient();
  const { track } = useAnalytics();
  const { t } = useTranslation();

  return useMutation({
    mutationFn: (transaction: Transaction) => {
      const hiddenAt = transaction.hiddenAt ? null : new Date();
      return updateTransaction({ id: transaction.id, hiddenAt });
    },
    onSuccess: (data) => {
      track(data.hiddenAt ? AnalyticsEvents.TRANSACTION_HIDDEN : AnalyticsEvents.TRANSACTION_UNHIDDEN);
      queryClient.invalidateQueries({ queryKey: TransactionsKeys.all });
      queryClient.invalidateQueries({ queryKey: CashFlowQueryKeys.all });
    },
    onError: () => snack.error(t('ENCOUNTERED_ERROR')),
  });
};

export const useToggleTransactionVerification = () => {
  const queryClient = useQueryClient();
  const { track } = useAnalytics();

  return useMutation({
    mutationFn: async (transaction: Transaction) => {
      const verificationStatus =
        transaction.verificationStatus === VerificationStatus.VERIFIED
          ? VerificationStatus.TO_VERIFY
          : VerificationStatus.VERIFIED;

      return await updateTransaction({ id: transaction.id, verificationStatus });
    },
    onSuccess: (transaction) => {
      if (transaction.verificationStatus === VerificationStatus.VERIFIED) {
        track(AnalyticsEvents.TRANSACTION_VERIFIED);
      }

      queryClient.invalidateQueries({ queryKey: TransactionsKeys.all });
      queryClient.invalidateQueries({ queryKey: CashFlowQueryKeys.all });
    },
    onError: (err, transaction) =>
      captureException(err, {
        extra: {
          transaction_id: transaction.id,
          verification_status: transaction.verificationStatus,
        },
      }),
  });
};

export const useMarkTransactionsUncategorized = () => {
  const companyId = useActiveCompanyId();
  const { track } = useAnalytics();
  return useMutation({
    mutationFn: ({ categoryId, subcategoryId }: { categoryId: Category['id']; subcategoryId: Subcategory['id'] }) =>
      bulkTransactionUpdate(
        { categoryId: null, subcategoryId: null },
        {
          companyId,
          category: {
            type: 'subcategories',
            categoryId,
            subcategoryId: [subcategoryId],
          },
          search: '',
          date: [null, null],
          accountId: [],
          currency: null,
          status: null,
          verificationStatus: null,
          transactionType: [],
          hidden: null,
          amountRange: [null, null, null],
          selected: [],
          excluded: [],
          cardHolder: [],
        },
      ),
    onSuccess() {
      queryClient.invalidateQueries({ queryKey: TransactionsKeys.all });
      track(AnalyticsEvents.TRANSACTION_CATEGORIES_BULK_UPDATED);
    },
  });
};

export const useTransactionsPreviewQuery = (filter: TransactionsPreviewFilter) => {
  const companyId = useActiveCompanyId();

  return useInfiniteQuery({
    queryKey: TransactionsKeys.preview(companyId, filter),
    queryFn: ({ pageParam }) =>
      fetchTransactionsPreview(companyId, filter, {
        cursor: pageParam,
        size: TransactionsPreviewPageSize,
      }),
    initialPageParam: '',
    getNextPageParam: ({ page: { cursor }, transactions }) => {
      if (transactions.length >= TransactionsPreviewPageSize) {
        return cursor;
      }
      return undefined;
    },
    enabled: Boolean(companyId),
    placeholderData: keepPreviousData,
  });
};

export const useTransactionQuery = (id: Transaction['id'], initialData?: Transaction) => {
  return useQuery({
    queryKey: TransactionsKeys.single(undefined, id),
    queryFn: () => {
      if (!id) {
        return;
      }
      return fetchTransactionDecorated(id);
    },
    structuralSharing: false,
    enabled: !!id,
    initialData,
  });
};

export const useSyncedDepositTransactionsQuery = (depositTransactionId: Transaction['id'] | null) => {
  const companyId = useActiveCompanyId();

  return useQuery({
    queryKey: TransactionsKeys.syncedDeposits(companyId, depositTransactionId),
    queryFn: () => {
      if (!depositTransactionId) return [];
      return fetchSyncedDepositTransactions(companyId, depositTransactionId);
    },
    enabled: !!companyId && !!depositTransactionId,
  });
};

export const useTransactionSuspenseQuery = (id: Transaction['id'] | null | undefined) => {
  return useSuspenseQuery({
    queryKey: TransactionsKeys.single(undefined, id || ''),
    queryFn: () => (id ? fetchTransactionDecorated(id) : null),
    structuralSharing: false,
  });
};

export const useTransactionsInfiniteQuery = (
  filters: TransactionsFiltersFields,
  sorting: SortInfo<TransactionSorting>,
  options?: { enabled: boolean },
) => {
  const companyId = useActiveCompanyId();

  return useInfiniteQuery({
    queryKey: TransactionsKeys.infiniteTable(companyId, filters, sorting),
    queryFn: ({ pageParam }) =>
      fetchTransactionsDecorated(companyId, filters, sorting, {
        size: TransactionsPageSize,
        cursor: pageParam,
      }),
    initialPageParam: '',
    getNextPageParam: ({
      meta: {
        page: { cursor },
      },
      data,
    }) => {
      if (data.length >= TransactionsPageSize) {
        return cursor;
      }
      return undefined;
    },
    placeholderData: keepPreviousData,
    enabled: Boolean(companyId) && Boolean(options?.enabled ?? true),
  });
};

export const useUpdateTransaction = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: updateTransaction,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: TransactionsKeys.all });
      queryClient.invalidateQueries({ queryKey: CashFlowQueryKeys.all });
    },
  });
};

export const useCreateTransaction = () => {
  const queryClient = useQueryClient();
  const companyId = useActiveCompanyId();

  return useMutation({
    mutationFn: (params: Omit<TransactionCreatePayload, 'company'>) =>
      createTransaction({ ...params, company: { id: companyId } }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: TransactionsKeys.all });
      queryClient.invalidateQueries({ queryKey: CashFlowQueryKeys.all });
      // refetchType: 'none' -> only mark accounts/balances (staleTime: Infinity) as stale, without
      // refetching now. Refetching immediately from the transactions view would mark the query
      // fresh, so the Conti page would never refetch on navigation (only a full browser refresh
      // would). Deferring lets it refetch when the Conti page mounts.
      queryClient.invalidateQueries({ queryKey: AccountQueryKeys.all, refetchType: 'none' });
      // Consents carry the per-account availableBalance shown in the account rows. Unlike accounts,
      // this query is always mounted (navigation sidebar), so it never remounts to trigger a
      // deferred refetch -> invalidate with the default active refetch so the sidebar observer
      // refetches it now and the Conti rows reflect the new balance.
      queryClient.invalidateQueries({ queryKey: ConsentQueryKeys.all });
    },
  });
};

export const useDeleteTransaction = () => {
  const { track } = useAnalytics();

  return useMutation({
    mutationFn: deleteTransaction,
    onSuccess: () => {
      track(AnalyticsEvents.TRANSACTION_DELETED);
      queryClient.invalidateQueries({ queryKey: TransactionsKeys.all });
      queryClient.invalidateQueries({ queryKey: CashFlowQueryKeys.all });
      // refetchType: 'none' -> see useCreateTransaction: defer the accounts/balances refetch to the
      // next Conti mount instead of firing it prematurely from the transactions view.
      queryClient.invalidateQueries({ queryKey: AccountQueryKeys.all, refetchType: 'none' });
      // See useCreateTransaction: consents hold the per-account balance and are always mounted, so
      // refetch them actively here.
      queryClient.invalidateQueries({ queryKey: ConsentQueryKeys.all });
    },
  });
};

export const useTransactionProposedRules = (direction?: TransactionRuleDirection) => {
  const companyId = useActiveCompanyId();

  return useQuery({
    queryKey: TransactionsKeys.proposedRules(companyId, direction),
    queryFn: () => fetchTransactionProposedRules(companyId, direction),
    enabled: !!companyId,
    initialData: [],
  });
};

export const useTransactionMetadata = (metadataFilters: Partial<TransactionMetadataFilter>) => {
  const companyId = useActiveCompanyId();

  const fullFilters = { ...defaultValues, ...metadataFilters };
  return useQuery({
    queryKey: TransactionsKeys.metadata(companyId, fullFilters),
    queryFn: () => fetchTransactionMetadata({ companyId, ...fullFilters }),
    enabled: !!companyId,
  });
};

export const useTransactionPreviewMetadata = (filter: TransactionsPreviewFilter) => {
  const companyId = useActiveCompanyId();

  return useQuery({
    queryKey: TransactionsKeys.metadataPreview(companyId, filter),
    queryFn: () => fetchTransactionsMetadataPreview(companyId, filter),
    enabled: Boolean(companyId),
  });
};

const reconciliations = create({
  fetcher: fetchTransactionReconciliationProposal,
  resolver: keyResolver('transactionId'),
  scheduler: windowedFiniteBatchScheduler({
    windowMs: 50,
    maxBatchSize: 50,
  }),
});

export const useTransactionReconciliation = (transactionId: Transaction['id'] | null, enabled = true) => {
  const companyId = useActiveCompanyId();

  return useQuery({
    queryFn: async () => (await reconciliations.fetch(transactionId ?? '')).flowIds,
    queryKey: TransactionsKeys.reconciliations(companyId, transactionId),
    enabled: enabled && !!transactionId && Boolean(companyId),
    refetchOnMount: false,
    refetchInterval: 300_000,
  });
};

export const useFirstAccountTransaction = (accountId?: string | null) => {
  const companyId = useActiveCompanyId();

  return useQuery({
    queryFn: () => {
      assertIsDefined(accountId, 'accountId must be defined');
      return fetchTransactions(
        { ...defaultValues, companyId, accountId: [accountId] },
        { sort: 'date', direction: 'asc' },
        { size: 1 },
      );
    },
    select: ({ data }) => data[0] ?? null,
    queryKey: TransactionsKeys.firstAccountTransaction(companyId, accountId ?? ''),
    enabled: Boolean(companyId) && Boolean(accountId),
  });
};

export const useLastAccountTransaction = (accountId?: string | null) => {
  const companyId = useActiveCompanyId();

  return useQuery({
    queryFn: () => {
      assertIsDefined(accountId, 'accountId must be defined');
      return fetchTransactions(
        { ...defaultValues, companyId, accountId: [accountId] },
        { sort: 'date', direction: 'desc' },
        { size: 1 },
      );
    },
    select: ({ data }) => data[0] ?? null,
    queryKey: TransactionsKeys.lastAccountTransaction(companyId, accountId ?? ''),
    enabled: Boolean(accountId) && Boolean(companyId),
  });
};

export const useTransactionsMetadata = (filters: TransactionMetadataFilter, enabled?: boolean) => {
  const companyId = useActiveCompanyId();

  return useQuery({
    queryKey: TransactionsKeys.metadataFields(companyId, filters),
    queryFn: () => fetchTransactionMetadata({ companyId, ...filters }),
    enabled: enabled !== false && Boolean(companyId),
  });
};

export const useShareCashbook = () => {
  const companyId = useActiveCompanyId();

  return useMutation({
    mutationFn: async (values: Omit<ShareCashbookParameters, 'companyId'>) => {
      return await shareCashbook({ companyId, ...values });
    },
  });
};

export const useDeduplicationOutcome = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: markDeduplicationOutcome,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: TransactionsKeys.all });
      queryClient.invalidateQueries({ queryKey: CashFlowQueryKeys.all });
    },
  });
};
